

import React, { useEffect, useState } from "react";
import {

  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Modal,
  TextInput,
  TableToolbarMenu,
  TableToolbarAction,
  Dropdown,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "village", header: "Village Name" },
  { key: "taluk", header: "Taluk" },
  { key: "district", header: "District" },
  { key: "state", header: "State" },
  { key: "country", header: "Country" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdAt", header: "Created At" },
  { key: "actions", header: "Actions" },
];

export default function VillageMaster() {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [taluks, setTaluks] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [villageName, setVillageName] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [errors, setErrors] = useState({});

  // Fetch Villages
  const fetchVillages = async () => {
    try {
      const response = await callApi("GET", "/villages/");
      const formattedRows = response.data.map((v) => ({
  id: Number(v.VILLAGE_ID),
  village: v.VILLAGE_NAME,
  talukId: Number(v.TALUK_ID),
  taluk: v.TALUK_NAME,
  districtId: Number(v.DISTRICT_ID),
  district: v.DISTRICT_NAME,
  stateId: Number(v.STATE_ID),
  state: v.STATE_NAME,
  countryId: Number(v.COUNTRY_ID),
  country: v.COUNTRY_NAME,
  status: v.STATUS,
  createdBy: v.created_by_username,
  createdAt: v.CREATED_TIMESTAMP,
  updatedAt: v.UPDATED_TIMESTAMP,
}));
      setRows(formattedRows);
    } catch (error) {
      console.error("Failed to fetch villages:", error.message);
    }
  };

  // Fetch supporting dropdown data
  const fetchDropdowns = async () => {
    try {
      const [distRes, stateRes, countryRes, talukRes] = await Promise.all([
        callApi("GET", "/districts/"),
        callApi("GET", "/state-masters/"),
        callApi("GET", "/countries/"),
        callApi("GET", "/taluk-masters/"),
      ]);

      setTaluks(
  talukRes.data.map((f) => ({
    id: Number(f.id),
    name: f.name,
    district_id: Number(f.district_id),
  }))
);
      setDistricts(
  distRes.data.map((d) => ({
    id: Number(d.id),
    name: d.name,
    state_id: Number(d.state_id),
  }))
);
      setStates(
  stateRes.data.map((s) => ({
    id: Number(s.id),
    name: s.state_name,
    country_id: Number(s.country_id),
  }))
);
      setCountries(
  countryRes.data.map((c) => ({
    id: Number(c.id),
    name: c.c_name,
    code: c.code,
  }))
);
    } catch (error) {
      console.error("Failed to fetch dropdowns:", error.message);
    }
  };

  const filteredRows = rows.filter((row) => {
  const search = searchTerm.toLowerCase();

  return (
    row.village?.toLowerCase().includes(search) ||
    row.taluk?.toLowerCase().includes(search) ||
    row.district?.toLowerCase().includes(search) ||
    row.state?.toLowerCase().includes(search) ||
    row.country?.toLowerCase().includes(search) ||
    row.status?.toLowerCase().includes(search) ||
    row.createdBy?.toLowerCase().includes(search)
  );
});

  useEffect(() => {
    fetchVillages();
    fetchDropdowns();
  }, []);

  // Open Modal
const openModal = (row = null) => {
  if (row) {
    setVillageName(row.village);
    setSelectedRow(row);

    const talukMatch = taluks.find(
      (t) => Number(t.id) === Number(row.talukId)
    );

    setSelectedTaluk(talukMatch || null);
  } else {
    setVillageName("");
    setSelectedRow(null);
    setSelectedTaluk(null);
  }

  setErrors({});
  setIsModalOpen(true);
};

  const resetModal = () => {
    setVillageName("");
    setSelectedRow(null);
    setSelectedTaluk(null);
    setErrors({});
    setIsModalOpen(false);
  };

  // Validate inputs
  const validateForm = () => {
    const newErrors = {};
    if (!villageName.trim()) newErrors.villageName = "Village name is required";
    if (!selectedTaluk) newErrors.taluk = "Taluk must be selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save Village
 const handleSave = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  if (!villageName.trim() || !selectedTaluk?.id) {
    console.error("Validation failed: missing villageName or talukId");
    return;
  }

  try {
    let payload;

    if (selectedRow) {
      // 🔑 Update payload
      payload = {
        VILLAGE_ID: Number(selectedRow.id),   // must be number
        VILLAGE_NAME: villageName.trim(),
        TALUK_ID: Number(selectedTaluk.id),
        UPDATED_BY_USER_ID: 1
      };

      console.log("PUT Payload:", payload);
      await callApi("PUT", "/villages/", payload);

    } else {
      // 🔑 Create payload
      payload = {
        VILLAGE_NAME: villageName.trim(),
        TALUK_ID: Number(selectedTaluk.id),
        CREATED_BY_USER_ID: 1,
        UPDATED_BY_USER_ID: 1
      };

      console.log("POST Payload:", payload);
      await callApi("POST", "/villages/", payload);
    }

    await fetchVillages();
    resetModal();
  } catch (error) {
    // 👇 log backend error details
    console.error(
      "Failed to save village:",
      error.response?.data || error.message
    );
  }
};


  // Delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await callApi("DELETE", `/villages/${deleteId}`);
      await fetchVillages();
    } catch (error) {
      console.error("Failed to delete village:", error.message);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // Auto-fill helpers
  const getDistrictForTaluk = (talukId) =>
    districts.find((d) => d.id === taluks.find((t) => t.id === talukId)?.district_id);

  const getStateForDistrict = (districtId) =>
    states.find((s) => s.id === districts.find((d) => d.id === districtId)?.state_id);

  const getCountryForState = (stateId) =>
    countries.find((c) => c.id === states.find((s) => s.id === stateId)?.country_id);

  const selectedDistrict = selectedTaluk
    ? getDistrictForTaluk(selectedTaluk.id)
    : null;
  const selectedState = selectedDistrict
    ? getStateForDistrict(selectedDistrict.id)
    : null;
  const selectedCountry = selectedState
    ? getCountryForState(selectedState.id)
    : null;

  return (
    <div>
      <TableContainer title="Village Master">
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch
  persistent
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  placeholder="Search villages"
/>
            <Button renderIcon={Add} onClick={() => openModal()}>
              Add Village
            </Button>
          </TableToolbarContent>
        </TableToolbar>

        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableHeader key={header.key}>{header.header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.village}</TableCell>
                <TableCell>{row.taluk}</TableCell>
                <TableCell>{row.district}</TableCell>
                <TableCell>{row.state}</TableCell>
                <TableCell>{row.country}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.createdBy}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>
                  <TableToolbarMenu>
                    <TableToolbarAction onClick={() => openModal(row)}>
                      Edit
                    </TableToolbarAction>
                    <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                      Delete
                    </TableToolbarAction>
                  </TableToolbarMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={resetModal}
        modalHeading={selectedRow ? "Edit Village" : "Add Village"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
      >
        <TextInput
          id="village-name"
          labelText="Village Name"
          value={villageName}
          onChange={(e) => setVillageName(e.target.value)}
          required
          invalid={!!errors.villageName}
          invalidText={errors.villageName}
        />

        <Dropdown
          id="taluk"
          titleText="Taluk"
          label="Select Taluk"
          items={taluks}
          itemToString={(item) => (item ? item.name : "")}
          selectedItem={selectedTaluk}
          onChange={({ selectedItem }) => setSelectedTaluk(selectedItem)}
          invalid={!!errors.taluk}
          invalidText={errors.taluk}
        />

        <TextInput
          id="district"
          labelText="District"
          value={selectedDistrict?.name || ""}
          disabled
        />

        <TextInput
          id="state"
          labelText="State"
          value={selectedState?.name || ""}
          disabled
        />

        <TextInput
          id="country"
          labelText="Country"
          value={selectedCountry?.name || ""}
          disabled
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this village?</p>
      </Modal>
    </div>
  );
}
